import { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';

const configured = Boolean(process.env.REACT_APP_FIREBASE_API_KEY && process.env.REACT_APP_FIREBASE_PROJECT_ID && process.env.REACT_APP_FIREBASE_AUTH_DOMAIN);
const app = configured ? (getApps()[0] || initializeApp({ apiKey: process.env.REACT_APP_FIREBASE_API_KEY, authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN, projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID })) : null;
const auth = app ? getAuth(app) : null;
const Context = createContext(null);
export async function request(path, options = {}) {
  const token = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
  const result = await fetch(path, { ...options, headers: { ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers } });
  let body;
  try { body = await result.json(); } catch { throw new Error('The content service is unavailable. Please try again later.'); }
  if (!result.ok) throw Object.assign(new Error(body.error || 'The request failed. Please try again.'), { status: result.status });
  return body;
}
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setAdmin] = useState(false);
  const [loading, setLoading] = useState(configured);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!auth) return undefined;
    let generation = 0;
    const unsubscribe = onAuthStateChanged(auth, async (next) => {
      const current = ++generation;
      setUser(next); setAdmin(false); setLoading(true); setError('');
      if (next) {
        try { const data = await request('/api/session'); if (current === generation) setAdmin(data.isAdmin); }
        catch (issue) { if (current === generation) setError(issue.message); }
      }
      if (current === generation) setLoading(false);
    });
    return () => { generation++; unsubscribe(); };
  }, []);
  async function login() {
    setError('');
    try { await signInWithPopup(auth, new GoogleAuthProvider()); }
    catch (issue) { setError(issue.code === 'auth/popup-closed-by-user' ? '' : 'Google sign-in did not finish. Please allow the sign-in popup and try again.'); }
  }
  return <Context.Provider value={{ user, isAdmin, loading, configured, error, login, logout: () => signOut(auth) }}>{children}</Context.Provider>;
}
export const useAuth = () => useContext(Context);
export function SignIn() {
  const { configured: ready, user, login, logout, error } = useAuth();
  return <div className="signin">{!ready ? <p>Sign-in is being set up. Please check back soon.</p> : user ? <><span>Signed in as {user.displayName || user.email}</span><button onClick={logout}>Sign out</button></> : <button onClick={login}>Sign in with Google</button>}{error && <p role="alert">{error}</p>}</div>;
}
